import React, { useEffect, useState } from 'react'
import { formatDateTime } from '@/utils/dateFormat'
import {
  getPhoneScreens,
  createPhoneScreen,
  updatePhoneScreen,
  cancelPhoneScreen,
  createNote,
  PhoneScreenCreateInput,
  PhoneScreenUpdateInput,
} from '@/api/tapResources'
import { TapPhoneScreen } from '@/types/index'
import { Modal } from '@/components/ui/Modal'
import { Calendar, Plus } from 'lucide-react'

// Feature #154 — Phone Screen tab. Booking, rescheduling, cancelling and
// recording outcomes. Multiple phone screens per application are allowed
// because candidates sometimes need follow-up calls.
interface PhoneScreenTabProps {
  applicationId: string
  // Feature #192: signal from parent to auto-open the "Book Phone Screen" modal.
  autoOpenBook?: boolean
  onAutoOpenHandled?: () => void
}

const RECOMMENDATIONS = ['Progress', 'Hold', 'Decline']

const cardStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-white)',
  border: '1px solid var(--color-light-grey)',
  borderRadius: '8px',
  padding: '20px',
  marginBottom: '16px',
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  border: '1px solid var(--color-light-grey)',
  borderRadius: '6px',
  fontSize: '14px',
  fontFamily: 'var(--font-family)',
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '12px',
  fontWeight: 600,
  color: 'var(--color-dark-grey)',
  textTransform: 'uppercase',
  marginBottom: '4px',
}

const primaryBtn: React.CSSProperties = {
  padding: '8px 16px',
  backgroundColor: 'var(--color-magenta)',
  color: 'var(--color-white)',
  border: 'none',
  borderRadius: '6px',
  fontSize: '13px',
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'var(--font-family)',
}
const secondaryBtn: React.CSSProperties = {
  ...primaryBtn,
  backgroundColor: 'var(--color-white)',
  color: 'var(--color-black)',
  border: '1px solid var(--color-light-grey)',
}

// Datetime-local inputs want "YYYY-MM-DDTHH:mm" without a timezone — trim any
// seconds/milliseconds/offset off an ISO string.
const toDatetimeLocal = (iso?: string) => {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number) => n.toString().padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}
const fromDatetimeLocal = (local: string) => (local ? new Date(local).toISOString() : undefined)

const PhoneScreenTab: React.FC<PhoneScreenTabProps> = ({ applicationId, autoOpenBook, onAutoOpenHandled }) => {
  const [items, setItems] = useState<TapPhoneScreen[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Book / edit modal state
  const [editing, setEditing] = useState<TapPhoneScreen | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [formScheduledAt, setFormScheduledAt] = useState('')
  const [formDuration, setFormDuration] = useState('')
  const [formLocation, setFormLocation] = useState('')
  const [formPrep, setFormPrep] = useState('')

  // Outcome modal state
  const [outcomeFor, setOutcomeFor] = useState<TapPhoneScreen | null>(null)
  const [outcomeSummary, setOutcomeSummary] = useState('')
  const [outcomeRecommendation, setOutcomeRecommendation] = useState('')

  const [saving, setSaving] = useState(false)

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      setItems(await getPhoneScreens(applicationId))
    } catch {
      setError('Unable to load phone screens')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId])

  // Feature #192: auto-open the booking modal when arriving from a transition.
  useEffect(() => {
    if (autoOpenBook && !loading) {
      openBook()
      onAutoOpenHandled?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenBook, loading])

  const openBook = () => {
    setEditing(null)
    setFormScheduledAt('')
    setFormDuration('30')
    setFormLocation('')
    setFormPrep('')
    setModalOpen(true)
  }

  const openEdit = (ps: TapPhoneScreen) => {
    setEditing(ps)
    setFormScheduledAt(toDatetimeLocal(ps.scheduledAt))
    setFormDuration(ps.durationMinutes?.toString() || '')
    setFormLocation(ps.locationOrLink || '')
    setFormPrep(ps.preparationNotes || '')
    setModalOpen(true)
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const payload: PhoneScreenCreateInput & PhoneScreenUpdateInput = {
        scheduledAt: fromDatetimeLocal(formScheduledAt),
        durationMinutes: formDuration ? parseInt(formDuration, 10) : undefined,
        locationOrLink: formLocation || undefined,
        preparationNotes: formPrep || undefined,
      }
      if (editing) {
        await updatePhoneScreen(applicationId, editing.id, payload)
      } else {
        await createPhoneScreen(applicationId, payload)
      }
      setModalOpen(false)
      await load()
    } catch {
      alert('Failed to save phone screen')
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = async (ps: TapPhoneScreen) => {
    if (!confirm('Cancel this phone screen?')) return
    try {
      await cancelPhoneScreen(applicationId, ps.id)
      await load()
    } catch {
      alert('Failed to cancel phone screen')
    }
  }

  const openOutcome = (ps: TapPhoneScreen) => {
    setOutcomeFor(ps)
    setOutcomeSummary(ps.outcomeSummary || '')
    setOutcomeRecommendation(ps.recommendation || '')
  }

  const handleOutcomeSave = async () => {
    if (!outcomeFor) return
    setSaving(true)
    try {
      await updatePhoneScreen(applicationId, outcomeFor.id, {
        outcomeSummary: outcomeSummary || undefined,
        recommendation: outcomeRecommendation || undefined,
      })
      // Feature #197: auto-create a note when the phone screen outcome is recorded.
      if (outcomeSummary) {
        try {
          await createNote(applicationId, `[Phone Screen Summary] ${outcomeSummary}`, 'PhoneScreen')
        } catch { /* supplementary */ }
      }
      setOutcomeFor(null)
      await load()
    } catch {
      alert('Failed to save outcome')
    } finally {
      setSaving(false)
    }
  }

  // iCal (.ics) hand-off — Requirements §3.7 calls for this explicitly so
  // reviewers can drop the call into Outlook / Google Calendar without a
  // manual copy-paste.
  const handleDownloadIcs = (ps: TapPhoneScreen) => {
    if (!ps.scheduledAt) {
      alert('This phone screen has no scheduled time yet.')
      return
    }
    const start = new Date(ps.scheduledAt)
    const end = new Date(start.getTime() + (ps.durationMinutes || 30) * 60000)
    const fmt = (d: Date) => d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z'
    const ics = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//TheLab//TAP//EN',
      'BEGIN:VEVENT',
      `UID:${ps.id}@thelab`,
      `DTSTAMP:${fmt(new Date())}`,
      `DTSTART:${fmt(start)}`,
      `DTEND:${fmt(end)}`,
      'SUMMARY:Phone Screen',
      ps.locationOrLink ? `LOCATION:${ps.locationOrLink}` : '',
      ps.preparationNotes ? `DESCRIPTION:${ps.preparationNotes.replace(/\n/g, '\\n')}` : '',
      'END:VEVENT',
      'END:VCALENDAR',
    ].filter(Boolean).join('\r\n')
    const blob = new Blob([ics], { type: 'text/calendar' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `phone-screen-${ps.id}.ics`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: 'var(--color-black)' }}>Phone Screens</h3>
        <button onClick={openBook} style={{ ...primaryBtn, display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <Plus size={14} /> Book phone screen
        </button>
      </div>

      {loading && <div style={{ color: 'var(--color-dark-grey)' }}>Loading…</div>}
      {error && <div style={{ color: 'var(--color-magenta)' }}>{error}</div>}

      {!loading && !error && items.length === 0 && (
        <div style={cardStyle}>
          <div style={{ color: 'var(--color-dark-grey)' }}>No phone screens booked yet.</div>
        </div>
      )}

      {!loading && items.map((ps) => (
        <div key={ps.id} style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: '14px' }}>
                {formatDateTime(ps.scheduledAt)}
                {ps.cancelledAt && (
                  <span style={{ marginLeft: '8px', fontSize: '12px', color: 'var(--color-magenta)' }}>Cancelled</span>
                )}
              </div>
              {ps.locationOrLink && (
                <div style={{ fontSize: '13px', color: 'var(--color-dark-grey)', marginTop: '2px' }}>
                  {ps.locationOrLink}
                </div>
              )}
              {ps.durationMinutes && (
                <div style={{ fontSize: '12px', color: 'var(--color-dark-grey)', marginTop: '2px' }}>
                  {ps.durationMinutes} minutes
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {!ps.cancelledAt && (
                <>
                  <button onClick={() => handleDownloadIcs(ps)} style={secondaryBtn}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      <Calendar size={13} /> iCal
                    </span>
                  </button>
                  <button onClick={() => openEdit(ps)} style={secondaryBtn}>Reschedule</button>
                  <button onClick={() => openOutcome(ps)} style={secondaryBtn}>
                    {ps.outcomeSummary ? 'Edit outcome' : 'Record outcome'}
                  </button>
                  <button
                    onClick={() => handleCancel(ps)}
                    style={{ ...secondaryBtn, color: 'var(--color-magenta)' }}
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>
          </div>
          {ps.preparationNotes && (
            <div style={{ fontSize: '13px', color: 'var(--color-dark-grey)', marginTop: '6px', whiteSpace: 'pre-wrap' }}>
              <strong>Prep:</strong> {ps.preparationNotes}
            </div>
          )}
          {ps.outcomeSummary && (
            <div style={{
              marginTop: '10px',
              padding: '10px 12px',
              backgroundColor: 'var(--color-light-grey)',
              borderRadius: '6px',
              fontSize: '13px',
            }}>
              <div style={{ fontWeight: 500, marginBottom: '4px' }}>
                Outcome{ps.recommendation ? ` — ${ps.recommendation}` : ''}
              </div>
              <div style={{ whiteSpace: 'pre-wrap', color: 'var(--color-dark-grey)' }}>{ps.outcomeSummary}</div>
            </div>
          )}
        </div>
      ))}

      {/* Book / edit modal */}
      <Modal
        isOpen={modalOpen}
        title={editing ? 'Reschedule phone screen' : 'Book phone screen'}
        onClose={() => setModalOpen(false)}
        isDirty={formScheduledAt !== '' || formLocation !== '' || formPrep !== ''}
        footer={
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button onClick={() => setModalOpen(false)} style={secondaryBtn}>Cancel</button>
            <button onClick={handleSave} style={primaryBtn} disabled={saving}>
              {saving ? 'Saving…' : editing ? 'Save' : 'Book'}
            </button>
          </div>
        }
      >
        <div style={{ display: 'grid', gap: '12px' }}>
          <div>
            <label style={labelStyle}>Scheduled at</label>
            <input
              type="datetime-local"
              value={formScheduledAt}
              onChange={(e) => setFormScheduledAt(e.target.value)}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Duration (minutes)</label>
            <input
              type="number"
              min={5}
              value={formDuration}
              onChange={(e) => setFormDuration(e.target.value)}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Location or call link</label>
            <input
              type="text"
              value={formLocation}
              onChange={(e) => setFormLocation(e.target.value)}
              placeholder="Teams/Zoom link or phone number"
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Preparation notes</label>
            <textarea
              value={formPrep}
              onChange={(e) => setFormPrep(e.target.value)}
              rows={4}
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>
        </div>
      </Modal>

      {/* Outcome modal */}
      <Modal
        isOpen={Boolean(outcomeFor)}
        title="Record phone screen outcome"
        onClose={() => setOutcomeFor(null)}
        isDirty={outcomeSummary !== '' || outcomeRecommendation !== ''}
        footer={
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button onClick={() => setOutcomeFor(null)} style={secondaryBtn}>Cancel</button>
            <button onClick={handleOutcomeSave} style={primaryBtn} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        }
      >
        <div style={{ display: 'grid', gap: '12px' }}>
          <div>
            <label style={labelStyle}>Recommendation</label>
            <select
              value={outcomeRecommendation}
              onChange={(e) => setOutcomeRecommendation(e.target.value)}
              style={{ ...inputStyle, backgroundColor: 'var(--color-white)', cursor: 'pointer' }}
            >
              <option value="">—</option>
              {RECOMMENDATIONS.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Summary</label>
            <textarea
              value={outcomeSummary}
              onChange={(e) => setOutcomeSummary(e.target.value)}
              rows={5}
              placeholder="What did you discuss? Any flags or highlights?"
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default PhoneScreenTab
